import { z } from 'zod';

/** V2 is an explicit opt-in. An absent version belongs to the legacy adapter. */
export const GENTV_RULES_VERSION_V2 = 2 as const;
export const GenTVStoryPreferencesV2Schema = z.object({
  format: z.enum(['drama', 'adventure']),
  genre: z.enum(['mystery', 'adventure', 'science-fiction', 'fantasy', 'comedy', 'thriller']),
  tone: z.enum(['light', 'adventurous', 'tense', 'heartfelt']),
  seed: z.string().trim().max(240),
}).strict();
export type GenTVStoryPreferencesV2 = z.infer<typeof GenTVStoryPreferencesV2Schema>;
export type GenTVCreationStepV2 = 'premise' | 'characters' | 'compiling' | 'review' | 'ready';
export type GenTVEpisodeStatusV2 = 'creating' | 'running' | 'finished' | 'ended';
export type GenTVCreationErrorCodeV2 = 'STALE_REVISION' | 'NOT_OWNER' | 'NOT_HOST' | 'INVALID_PHASE' | 'INVALID_INPUT' | 'CAPACITY' | 'JOB_PENDING' | 'DRAFT_REJECTED' | 'CAPABILITY_REQUIRED' | 'EPISODE_ENDED' | 'REQUEST_ID_REUSED' | 'LIMIT_REACHED';

export const GenTVCharacterFieldsV2Schema = z.object({
  name: z.string().trim().max(40),
  identity: z.string().trim().max(120),
  want: z.string().trim().max(160),
  capability: z.string().trim().max(120),
  limitation: z.string().trim().max(120),
  appearance: z.string().trim().max(240),
  sex: z.enum(['male', 'female']).optional(),
  /** Read compatibility for existing approved episodes; no longer authored by the UI or suggestion writer. */
  pronouns: z.string().trim().max(40).optional(),
  presentationMode: z.enum(['authored', 'photo']),
  adult: z.literal(true),
}).strict();
export type GenTVCharacterFieldsV2 = z.infer<typeof GenTVCharacterFieldsV2Schema>;
export interface GenTVContributionV2 { id: string; revision: number; text: string }
export interface GenTVContributionDispositionV2 {
  contributionId: string;
  disposition: 'used' | 'adapted' | 'not_used';
  explanation: string;
}
export interface GenTVPitchV2 extends GenTVStoryPreferencesV2 {
  id: string;
  title: string;
  logline: string;
  objective: string;
  stakes: string;
  contributionDispositions: GenTVContributionDispositionV2[];
}
export interface GenTVPitchBatchV2 {
  id: string;
  revision: number;
  inputHash: string;
  source: 'deterministic_fallback' | 'writer';
  writerVersion: string;
  validatorVersion: string;
  pitches: [GenTVPitchV2, GenTVPitchV2, GenTVPitchV2];
  openedAt: number;
  deadline: number;
  /** Server-only authenticated participant IDs. Public projections contain counts only. */
  ballots: Record<string, string>;
}
export interface GenTVCharacterApprovalV2 {
  characterRevision: number;
  publicHash: string;
  privateHash: string;
  approvedAt: number;
}
export interface GenTVCharacterDraftV2 {
  characterId: string;
  claimId: string;
  ownerId: string;
  revision: number;
  publicFields: GenTVCharacterFieldsV2;
  /** Private author data and consent never become part of a public spread. */
  secret?: string;
  photoConsent: boolean;
  /** Owner delegates fictional character, relationships and briefing generation for this revision. */
  quickConsent?: { revision: number; acceptedAt: number };
  approval?: GenTVCharacterApprovalV2;
  suggestion?: { sourceRevision: number; publicFields: GenTVCharacterFieldsV2; source?: 'writer' | 'deterministic_fallback'; failureCode?: string };
}
export interface GenTVRelationshipV2 {
  id: string;
  revision: number;
  fromCharacterId: string;
  toCharacterId: string;
  fromCharacterRevision: number;
  toCharacterRevision: number;
  text: string;
  kind: 'shared_fact' | 'perspective';
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  proposedAt: number;
  deadline: number;
}
export interface GenTVFrozenCharacterV2 {
  characterId: string;
  claimId: string;
  ownerId: string;
  revision: number;
  publicFields: GenTVCharacterFieldsV2;
  secret?: string;
  photoConsent: boolean;
  approval: GenTVCharacterApprovalV2;
}
/** Restricted compiler input. Never send this object to a public writer or client. */
export interface GenTVFrozenCompilationInputsV2 {
  rulesVersion: 2;
  episodeId: string;
  inputHash: string;
  creationRevision: number;
  premise: GenTVPitchV2;
  characters: GenTVFrozenCharacterV2[];
  relationships: GenTVRelationshipV2[];
  /** L4 adds only needed support: solo requires one NPC, never more than two. */
  supportingNpcPolicy: { minimum: number; maximum: 2; maximumTotalCast: 6 };
}
export interface GenTVCreationReplyV2 {
  ok: boolean;
  revision: number;
  code?: GenTVCreationErrorCodeV2;
  error?: string;
  characterId?: string;
  claimId?: string;
  batchId?: string;
  jobId?: string;
  inputHash?: string;
}
export interface GenTVCreationReceiptV2 {
  participantId: string;
  requestId: string;
  commandHash: string;
  reply: GenTVCreationReplyV2;
}
export interface GenTVCompilationJobV2 {
  jobId: string;
  inputHash: string;
  reservedAt: number;
  deadline: number;
  status: 'reserved' | 'dispatched' | 'completed' | 'failed' | 'expired' | 'cancelled';
  dispatchedAt?: number;
  failureCode?: 'DRAFT_REJECTED' | 'DEADLINE' | 'UNAVAILABLE';
  /** Live writer stage for every screen's progress strip; never carries story text. */
  progress?: GenTVCompilationProgressV2;
}
export interface GenTVCompilationProgressV2 {
  stage: 'write' | 'repair' | 'critic';
  attempt: number;
  maxAttempts: number;
  /** When this stage started (server clock). */
  since: number;
  /** Set when the previous draft failed a check, e.g. INVALID_SCHEMA. */
  previousFailure?: string;
}
export interface GenTVCreationJobV2 {
  jobId: string;
  kind: 'pitches' | 'character';
  episodeId: string;
  requesterId: string;
  reservedAt: number;
  deadline: number;
  status: 'reserved' | 'dispatched' | 'completed' | 'failed' | 'expired' | 'cancelled';
  dispatchedAt?: number;
  inputHash?: string;
  batchId?: string;
  characterId?: string;
  claimId?: string;
  characterRevision?: number;
  hint?: string;
  failureCode?: 'DEADLINE' | 'UNAVAILABLE' | 'VALIDATION_FAILED';
  source?: 'writer' | 'deterministic_fallback';
}
export interface GenTVStoryPreviewV2 {
  rulesVersion: 2;
  bibleId: string;
  revision: number;
  title: string;
  logline: string;
  place: string;
  groupObjective: string;
  stakes: string;
  cast: { characterId: string; name: string; identity: string; appearance: string; want?: string; capability?: string; limitation?: string; sex?: 'male' | 'female' }[];
}
export interface GenTVStoryBriefingV2 {
  bibleId: string;
  revision: number;
  characterId: string;
  text: string;
  secret?: string;
  proposedGoal?: string;
  knownFacts: { factId: string; value: string | number | boolean }[];
}
export interface GenTVStoryReviewV2 {
  inputHash: string;
  preview: GenTVStoryPreviewV2;
  /** Server-only map; never broadcast or expose by host privilege. */
  briefings: Record<string, GenTVStoryBriefingV2>;
  hostApproved: boolean;
  ownerApprovals: Record<string, { claimId: string; briefingHash: string; acceptGoal: boolean }>;
}
export const GenTVQuickStoryV2Schema = z.object({
  preferences: GenTVStoryPreferencesV2Schema,
  premise: z.object({ title: z.string().trim().min(1).max(60), logline: z.string().trim().min(1).max(300), objective: z.string().trim().min(1).max(160), stakes: z.string().trim().min(1).max(200) }).strict(),
  characters: z.array(z.object({ characterId: z.string().min(1).max(128), fields: GenTVCharacterFieldsV2Schema.omit({ pronouns: true }) }).strict()).min(1).max(4),
  relationships: z.array(z.object({ fromCharacterId: z.string().min(1).max(128), toCharacterId: z.string().min(1).max(128), text: z.string().trim().min(1).max(160) }).strict()).max(4),
}).strict();
export type GenTVQuickStoryV2 = z.infer<typeof GenTVQuickStoryV2Schema>;
export const GENTV_QUICK_GENERATION_VERSION_V2 = 2 as const;
export interface GenTVQuickSetupV2 {
  /** Server-owned contract version; old failed generators do not consume the corrected generator’s retry allowance. */
  generationVersion?: number;
  previousAttempts?: number;
  jobId: string; requesterId: string; inputHash: string; deadline: number; attempts: number;
  status: 'reserved' | 'dispatched' | 'portraits' | 'compiled' | 'failed';
  generated?: boolean;
  failureCode?: 'DEADLINE' | 'UNAVAILABLE' | 'VALIDATION_FAILED' | 'PARTICIPANT_LEFT' | 'PORTRAIT_FAILED';
  /** The failure happened while the script was being built, not while writing the story or portraits. */
  failedDuringScript?: boolean;
}
/** Persist under the existing episode mutation lock, atomically with receipts. */
export interface GenTVCreationStateV2 {
  /** Absent on existing episodes: retain their manual workflow. */
  setupMode?: 'quick' | 'custom';
  quickSetup?: GenTVQuickSetupV2;
  preferencesCustomized?: boolean;
  /** Host setting frozen into both decision windows at Begin; absent legacy v2 state uses 45. */
  decisionDurationSeconds?: number;
  rulesVersion: 2;
  episodeId: string;
  roomId: string;
  revision: number;
  status: GenTVEpisodeStatusV2;
  creationStep: GenTVCreationStepV2;
  preferencesRevision: number;
  preferences: GenTVStoryPreferencesV2;
  contributions: Record<string, GenTVContributionV2>;
  /** Authenticated room roster; never expose participant IDs in the public projection. */
  activeParticipantIds?: string[];
  creationJobs?: GenTVCreationJobV2[];
  currentPitchJobId?: string;
  pitchBatchesUsed: number;
  pitchBatch?: GenTVPitchBatchV2;
  selectedPremise?: GenTVPitchV2;
  characters: Record<string, GenTVCharacterDraftV2>;
  relationships: GenTVRelationshipV2[];
  frozenInputs?: GenTVFrozenCompilationInputsV2;
  compilationJobs?: GenTVCompilationJobV2[];
  currentCompilationJobId?: string;
  storyReview?: GenTVStoryReviewV2;
  commandReceipts: GenTVCreationReceiptV2[];
}
export interface GenTVPublicCharacterV2 {
  quickReady?: boolean;
  characterId: string;
  revision: number;
  fields: GenTVCharacterFieldsV2;
  approved: boolean;
}
export interface GenTVPublicPitchBatchV2 {
  id: string;
  source: 'deterministic_fallback' | 'writer';
  revision: number;
  pitches: [GenTVPitchV2, GenTVPitchV2, GenTVPitchV2];
  deadline: number;
  voteCounts: Record<string, number>;
  recommendedPitchIds: string[];
  allActiveVotersBalloted?: boolean;
}
export interface GenTVPublicCreationV2 {
  setupMode?: 'quick' | 'custom';
  quickSetup?: Pick<GenTVQuickSetupV2, 'status' | 'deadline' | 'failureCode' | 'generated' | 'attempts' | 'failedDuringScript'>;
  decisionDurationSeconds?: number;
  rulesVersion: 2;
  episodeId: string;
  roomId: string;
  revision: number;
  status: GenTVEpisodeStatusV2;
  creationStep: GenTVCreationStepV2;
  preferencesRevision: number;
  pitchInputHash: string;
  preferences: GenTVStoryPreferencesV2;
  contributions: GenTVContributionV2[];
  pitchBatchesUsed: number;
  pitchBatch?: GenTVPublicPitchBatchV2;
  pitchGeneration?: { status: GenTVCreationJobV2['status']; deadline: number };
  selectedPremise?: GenTVPitchV2;
  selectionLabel?: string;
  characters: GenTVPublicCharacterV2[];
  relationships: GenTVRelationshipV2[];
  canCompile: boolean;
  compilation?: { status: GenTVCompilationJobV2['status']; deadline: number; failureCode?: GenTVCompilationJobV2['failureCode']; dispatchedAt?: number; progress?: GenTVCompilationProgressV2 };
  storyReview?: { preview: GenTVStoryPreviewV2; hostApproved: boolean; readyCharacterIds: string[] };
  serverTime: number;
}
/** Only deliver to the authenticated owner; host status conveys no extra access. */
export interface GenTVPrivateCreationV2 {
  rulesVersion: 2;
  episodeId: string;
  revision: number;
  contribution?: GenTVContributionV2;
  ballotPitchId?: string;
  briefing?: GenTVStoryBriefingV2 & { hash: string; approved: boolean; acceptGoal?: boolean };
  character?: {
    characterId: string;
    claimId: string;
    revision: number;
    fields: GenTVCharacterFieldsV2;
    secret?: string;
    photoConsent: boolean;
    approved: boolean;
    publicHash: string;
    privateHash: string;
    suggestion?: { sourceRevision: number; publicFields: GenTVCharacterFieldsV2; source?: 'writer' | 'deterministic_fallback'; failureCode?: string };
    suggestionJob?: { status: GenTVCreationJobV2['status']; deadline: number };
  };
}
const id = z.string().min(1).max(128);
const revision = z.number().int().nonnegative();
const envelope = { episodeId: id, requestId: id };
const claim = { characterId: id, claimId: id, expectedRevision: revision };
export const GenTVCreationCommandV2Schema = z.discriminatedUnion('type', [
  z.object({ ...envelope, type: z.literal('set_setup_mode_v2'), expectedRevision: revision, mode: z.enum(['quick', 'custom']) }).strict(),
  z.object({ ...envelope, type: z.literal('ready_quick_character_v2'), expectedRevision: revision, sex: z.enum(['male', 'female']), photo: z.string().max(2_000_000).nullable(), customFields: z.object({ name: z.string().trim().max(40), identity: z.string().trim().max(120), want: z.string().trim().max(160), capability: z.string().trim().max(120), limitation: z.string().trim().max(120), appearance: z.string().trim().max(240) }).strict().optional() }).strict(),
  z.object({ ...envelope, type: z.literal('start_quick_story_v2'), expectedRevision: revision }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('leave_quick_story_v2') }).strict(),
  z.object({ ...envelope, type: z.literal('set_decision_duration_v2'), expectedRevision: revision, seconds: z.number().int().min(30).max(60) }).strict(),
  z.object({ ...envelope, type: z.literal('set_story_preferences_v2'), expectedRevision: revision, preferences: GenTVStoryPreferencesV2Schema }).strict(),
  z.object({ ...envelope, type: z.literal('upsert_story_idea_v2'), expectedRevision: revision, text: z.string().trim().min(1).max(240) }).strict(),
  z.object({ ...envelope, type: z.literal('withdraw_story_idea_v2'), expectedRevision: revision }).strict(),
  z.object({ ...envelope, type: z.literal('generate_pitches_v2'), inputHash: id }).strict(),
  z.object({ ...envelope, type: z.literal('vote_pitch_v2'), batchId: id, expectedRevision: revision, pitchId: id }).strict(),
  z.object({ ...envelope, type: z.literal('select_pitch_v2'), batchId: id, expectedRevision: revision, pitchId: id }).strict(),
  z.object({ ...envelope, type: z.literal('claim_character_v2') }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('release_character_v2') }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('save_character_draft_v2'), publicFields: GenTVCharacterFieldsV2Schema.optional(), secret: z.string().trim().max(240).nullable().optional(), photoConsent: z.boolean().optional() }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('suggest_character_v2'), mode: z.enum(['help', 'surprise']), hint: z.string().trim().max(240).optional() }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('approve_character_v2'), publicHash: id, privateHash: id }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('propose_relationship_v2'), toCharacterId: id, expectedTargetRevision: revision, text: z.string().trim().min(1).max(160), kind: z.enum(['shared_fact', 'perspective']) }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('answer_relationship_v2'), proposalId: id, expectedProposalRevision: revision, answer: z.enum(['accept', 'decline']) }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('reopen_character_v2') }).strict(),
  z.object({ ...envelope, type: z.literal('compile_story_v2'), expectedRevision: revision }).strict(),
  z.object({ ...envelope, type: z.literal('approve_story_v2'), bibleId: id, bibleRevision: revision }).strict(),
  z.object({ ...envelope, ...claim, type: z.literal('approve_briefing_v2'), bibleId: id, bibleRevision: revision, briefingHash: id, acceptGoal: z.boolean() }).strict(),
  z.object({ ...envelope, type: z.literal('revise_creation_v2'), expectedRevision: revision, step: z.enum(['premise', 'characters']) }).strict(),
]);
export type GenTVCreationCommandV2 = z.infer<typeof GenTVCreationCommandV2Schema>;
